// @ts-check

/**
 * @typedef {{ id: number, name: string, price: number }} SellingPlanOption
 */

class FrankMultiSelector extends HTMLElement {
  /** @type {HTMLElement[]} */
  cards = [];
  /** @type {HTMLButtonElement | null} */
  submitButton = null;
  /** @type {HTMLElement | null} */
  submitText = null;
  /** @type {HTMLElement | null} */
  inlinePriceEl = null;
  /** @type {HTMLElement | null} */
  priceSeparatorEl = null;
  /** @type {HTMLElement | null} */
  errorEl = null;
  /** @type {HTMLElement | null} */
  purchaseEl = null;
  /** @type {HTMLSelectElement | null} */
  frequencySelect = null;
  /** @type {HTMLElement | null} */
  frequencyWrap = null;
  /** @type {string} */
  defaultButtonText = 'Add to Cart';
  /** @type {'subscription' | 'onetime'} */
  mode = 'subscription';
  /** @type {string} */
  frequencyName = '';
  /** @type {boolean} */
  anyRequiresSubscription = false;
  /** @type {number} */
  minRegularCents = 0;
  /** @type {number} */
  minSubscriptionCents = 0;

  connectedCallback() {
    this.cards = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[data-card]')).filter(
        (el) => el instanceof HTMLElement
      )
    );
    this.submitButton = this.querySelector('[data-submit]');
    this.submitText = this.querySelector('[data-submit-text]');
    this.inlinePriceEl = this.querySelector('[data-inline-price]');
    this.priceSeparatorEl = this.querySelector('[data-price-separator]');
    this.errorEl = this.querySelector('[data-error]');
    this.purchaseEl = this.querySelector('[data-purchase-options]');
    this.frequencySelect = this.querySelector('[data-frequency]');
    this.frequencyWrap = this.querySelector('[data-frequency-wrap]');
    this.defaultButtonText =
      this.dataset.buttonText ||
      (this.submitText && this.submitText.textContent
        ? this.submitText.textContent.trim()
        : 'Add to Cart');

    this.anyRequiresSubscription = Boolean(
      this.purchaseEl &&
        this.purchaseEl.dataset.anyRequiresSubscription === 'true'
    );

    const minReg = parseInt(this.dataset.minRegular || '0', 10) || 0;
    const minSub = parseInt(this.dataset.minSubscription || '0', 10) || 0;
    this.minRegularCents = minReg > 0 ? minReg : 0;
    this.minSubscriptionCents = minSub > 0 ? minSub : minReg;

    // If no toggle UI is rendered, but any variant requires subscription,
    // keep mode locked to subscription. Otherwise default to subscription
    // when the selector exists (matches reference layout default).
    this.mode = 'subscription';

    this.populateFrequencyOptions();

    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);

    this.updateUI();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
  }

  populateFrequencyOptions() {
    if (!this.purchaseEl || !this.frequencySelect) return;
    /** @type {SellingPlanOption[]} */
    let plans = [];
    try {
      plans = JSON.parse(this.purchaseEl.dataset.frequencyOptions || '[]');
    } catch (_) {
      plans = [];
    }

    this.frequencySelect.innerHTML = '';
    const firstPlan = plans[0];
    if (!firstPlan) {
      if (this.frequencyWrap) this.frequencyWrap.hidden = true;
      return;
    }

    plans.forEach((plan, index) => {
      const opt = document.createElement('option');
      opt.value = plan.name;
      opt.textContent = plan.name + (index === 0 ? ' (most popular)' : '');
      this.frequencySelect?.appendChild(opt);
    });

    this.frequencyName = firstPlan.name;
    this.frequencySelect.value = this.frequencyName;
    if (this.frequencyWrap) this.frequencyWrap.hidden = false;
  }

  /** @param {Event} event */
  onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const qtyBtn = target.closest('[data-action]');
    if (qtyBtn instanceof HTMLButtonElement && this.contains(qtyBtn)) {
      event.preventDefault();
      this.adjustQty(qtyBtn);
      return;
    }

    const submitBtn = target.closest('[data-submit]');
    if (submitBtn && this.contains(submitBtn)) {
      event.preventDefault();
      this.submit();
    }
  }

  /** @param {Event} event */
  onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target instanceof HTMLInputElement && target.dataset.purchaseMode) {
      this.mode =
        target.value === 'onetime' && !this.anyRequiresSubscription
          ? 'onetime'
          : 'subscription';
      this.updateUI();
      return;
    }

    if (target === this.frequencySelect && this.frequencySelect) {
      this.frequencyName = this.frequencySelect.value;
      this.updateUI();
    }
  }

  /** @param {HTMLButtonElement} button */
  adjustQty(button) {
    if (button.disabled) return;
    const card = button.closest('[data-card]');
    if (!(card instanceof HTMLElement)) return;
    const valueEl = card.querySelector('[data-qty-value]');
    if (!valueEl) return;
    let qty = parseInt(valueEl.textContent || '0', 10) || 0;
    qty += button.dataset.action === 'increase' ? 1 : -1;
    if (qty < 0) qty = 0;
    valueEl.textContent = String(qty);
    card.dataset.active = qty > 0 ? 'true' : 'false';
    this.updateUI();
  }

  /**
   * Lowest single-unit subscription price across all cards at the currently
   * selected frequency. Falls back to the cached minimum if no frequency
   * pricing can be resolved.
   * @returns {number}
   */
  getMinSubscriptionCentsForFrequency() {
    let min = -1;
    this.cards.forEach((card) => {
      const { price } = this.resolveSubscriptionPricing(card);
      if (price > 0 && (min === -1 || price < min)) {
        min = price;
      }
    });
    return min === -1 ? this.minSubscriptionCents : min;
  }

  /**
   * Returns the subscription price + selling-plan id for a card at the
   * currently selected frequency, regardless of mode.
   * @param {HTMLElement} card
   * @returns {{ price: number, planId: string }}
   */
  resolveSubscriptionPricing(card) {
    /** @type {SellingPlanOption[]} */
    let plans = [];
    try {
      plans = JSON.parse(card.dataset.sellingPlans || '[]');
    } catch (_) {
      plans = [];
    }

    if (this.frequencyName) {
      const match = plans.find((p) => p.name === this.frequencyName);
      if (match) {
        return { price: match.price, planId: String(match.id) };
      }
    }
    const firstPlan = plans[0];
    if (firstPlan) {
      return { price: firstPlan.price, planId: String(firstPlan.id) };
    }
    const fallback = parseInt(card.dataset.variantPriceSubscription || '0', 10) || 0;
    return { price: fallback, planId: '' };
  }

  /**
   * Returns the price + selling-plan id to use for a given card under the
   * current mode + frequency.
   * @param {HTMLElement} card
   * @returns {{ price: number, planId: string }}
   */
  resolveCardPricing(card) {
    const regularPrice = parseInt(card.dataset.variantPriceRegular || '0', 10) || 0;
    const requiresSub = card.dataset.requiresSubscription === 'true';

    if (this.mode === 'onetime' && !requiresSub) {
      return { price: regularPrice, planId: '' };
    }

    const sub = this.resolveSubscriptionPricing(card);
    if (sub.planId || sub.price > 0) {
      return sub;
    }
    return { price: regularPrice, planId: '' };
  }

  /**
   * @returns {{ items: Array<{ id: string, quantity: number, planId: string }>, totalCents: number, totalRegularCents: number, totalSubscriptionCents: number }}
   */
  collect() {
    /** @type {Array<{ id: string, quantity: number, planId: string }>} */
    const items = [];
    let totalCents = 0;
    let totalRegularCents = 0;
    let totalSubscriptionCents = 0;

    this.cards.forEach((card) => {
      const valueEl = card.querySelector('[data-qty-value]');
      const qty = parseInt((valueEl && valueEl.textContent) || '0', 10) || 0;
      if (qty <= 0) return;

      const regular = parseInt(card.dataset.variantPriceRegular || '0', 10) || 0;
      const subPricing = this.resolveSubscriptionPricing(card);
      const sub = subPricing.price > 0 ? subPricing.price : regular;
      totalRegularCents += qty * regular;
      totalSubscriptionCents += qty * sub;

      const { price, planId } = this.resolveCardPricing(card);
      totalCents += qty * price;

      const variantId = card.dataset.variantId || '';
      if (variantId) {
        items.push({ id: variantId, quantity: qty, planId });
      }
    });

    return { items, totalCents, totalRegularCents, totalSubscriptionCents };
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  formatMoney(cents) {
    const currency = (Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol',
      }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2);
    }
  }

  updateUI() {
    const { items, totalCents, totalRegularCents, totalSubscriptionCents } = this.collect();

    if (this.inlinePriceEl) {
      this.inlinePriceEl.textContent = totalCents > 0 ? this.formatMoney(totalCents) : '';
    }
    if (this.priceSeparatorEl) {
      this.priceSeparatorEl.hidden = totalCents <= 0;
    }
    if (this.submitButton) {
      this.submitButton.disabled = items.length === 0;
    }

    this.cards.forEach((card) => {
      const valueEl = card.querySelector('[data-qty-value]');
      const qty = parseInt((valueEl && valueEl.textContent) || '0', 10) || 0;
      const minusBtn = card.querySelector('[data-decrease]');
      if (minusBtn instanceof HTMLButtonElement) {
        minusBtn.disabled = qty <= 0;
      }
    });

    // If nothing is selected yet, show a preview of pricing for 1 unit of the
    // cheapest product in each mode so the option boxes always have prices.
    const previewRegular =
      totalRegularCents > 0 ? totalRegularCents : this.minRegularCents;
    const previewSubscription =
      totalSubscriptionCents > 0
        ? totalSubscriptionCents
        : this.getMinSubscriptionCentsForFrequency();
    this.updateOptionCards(previewRegular, previewSubscription);

    this.clearError();
  }

  /**
   * Update the prices + selected styling shown inside the subscription /
   * one-time option cards.
   * @param {number} regularCents
   * @param {number} subCents
   */
  updateOptionCards(regularCents, subCents) {
    if (!this.purchaseEl) return;

    const subCard = this.purchaseEl.querySelector('[data-mode-card="subscription"]');
    const oneCard = this.purchaseEl.querySelector('[data-mode-card="onetime"]');

    if (subCard instanceof HTMLElement) {
      const compare = subCard.querySelector('[data-mode-compare]');
      const sale = subCard.querySelector('[data-mode-sale]');
      const save = subCard.querySelector('[data-mode-save]');
      const hasItems = subCents > 0;
      const hasDiscount = hasItems && subCents < regularCents;

      if (compare instanceof HTMLElement) {
        compare.textContent = hasDiscount ? this.formatMoney(regularCents) : '';
        compare.hidden = !hasDiscount;
      }
      if (sale instanceof HTMLElement) {
        sale.textContent = hasItems ? this.formatMoney(subCents) : '';
      }
      if (save instanceof HTMLElement) {
        if (hasDiscount) {
          const pct = Math.round(((regularCents - subCents) / regularCents) * 100);
          save.textContent = `SAVE ${pct}%`;
          save.hidden = false;
        } else {
          save.textContent = '';
          save.hidden = true;
        }
      }

      subCard.dataset.selected = this.mode === 'subscription' ? 'true' : 'false';
    }

    if (oneCard instanceof HTMLElement) {
      const sale = oneCard.querySelector('[data-mode-sale]');
      if (sale instanceof HTMLElement) {
        sale.textContent = regularCents > 0 ? this.formatMoney(regularCents) : '';
      }
      oneCard.dataset.selected = this.mode === 'onetime' ? 'true' : 'false';
    }
  }

  /** @param {boolean} busy */
  setBusy(busy) {
    if (!this.submitButton) return;
    this.submitButton.disabled = busy;
    if (this.submitText) {
      this.submitText.textContent = busy ? 'Adding...' : this.defaultButtonText;
    }
  }

  /** @param {string} message */
  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
  }

  async submit() {
    const { items } = this.collect();
    if (items.length === 0) return;

    this.setBusy(true);
    this.clearError();

    /** @type {any} */
    const themeGlobal = /** @type {any} */ (globalThis).Theme;
    const cartAddUrl =
      (themeGlobal && themeGlobal.routes && themeGlobal.routes.cart_add_url) ||
      '/cart/add.js';
    const cartUrl =
      (themeGlobal && themeGlobal.routes && themeGlobal.routes.cart_url) ||
      '/cart';

    const payload = {
      items: items.map((item) => {
        /** @type {{ id: number, quantity: number, selling_plan?: number }} */
        const entry = { id: Number(item.id), quantity: item.quantity };
        if (item.planId) entry.selling_plan = Number(item.planId);
        return entry;
      }),
    };

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });

      const responseBody = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          responseBody.description ||
            responseBody.message ||
            `Cart returned ${response.status}`
        );
      }

      let cart = null;
      try {
        cart = await fetch(`${cartUrl}.js`).then((r) => r.json());
      } catch (_) {
        /* non-fatal */
      }

      this.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'frank-multi-product-selector',
            data: { source: 'frank-multi-product-selector' },
          },
        })
      );

      this.resetQuantities();
      if (this.submitText) {
        this.submitText.textContent = 'Added';
      }
      setTimeout(() => this.setBusy(false), 1200);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      console.error('[frank-multi-product-selector] add to cart failed', err);
      this.showError(message);
      this.setBusy(false);
    } finally {
      this.updateUI();
    }
  }

  resetQuantities() {
    this.cards.forEach((card) => {
      const valueEl = card.querySelector('[data-qty-value]');
      if (valueEl) valueEl.textContent = '0';
      card.dataset.active = 'false';
    });
  }
}

if (!customElements.get('frank-multi-selector')) {
  customElements.define('frank-multi-selector', FrankMultiSelector);
}
